import { motion } from 'framer-motion';
import { Heart, Instagram, Camera, ScanBarcode } from 'lucide-react';
import sneakersImg from '@/assets/onboarding/sneakers.jpg';
import dressImg from '@/assets/onboarding/dress.jpg';
import handbagImg from '@/assets/onboarding/handbag.jpg';

type Source = {
  name: string;
  subtitle: string;
  delay: number;
} & ({ type: 'image'; image: string } | { type: 'icon'; icon: React.ReactNode; bg: string });

const sources: Source[] = [
  { name: 'Nike.com', subtitle: 'Saved from retailer', type: 'image', image: sneakersImg, delay: 0.3 },
  { name: 'Instagram', subtitle: 'Saved from social', type: 'icon', icon: <Instagram className="w-5 h-5 text-white" />, bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', delay: 0.45 },
  { name: 'Camera Roll', subtitle: 'Saved from photo', type: 'image', image: dressImg, delay: 0.6 },
  { name: 'Barcode Scan', subtitle: 'Scanned in store', type: 'icon', icon: <ScanBarcode className="w-5 h-5 text-white" />, bg: 'bg-emerald-500', delay: 0.75 },
  { name: 'ASOS', subtitle: 'Saved from browser', type: 'image', image: handbagImg, delay: 0.9 },
];

export function SaveAnywhereMockup({ isActive = true }: { isActive?: boolean }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-b from-muted to-background flex items-center justify-center px-6 pt-4 pb-2">
      <div className="w-full max-w-[280px] relative">

        {/* Source cards */}
        <div className="space-y-2">
          {sources.map((source) => (
            <motion.div
              key={source.name}
              initial={{ opacity: 0, y: 24, scale: 0.9 }}
              animate={isActive ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 24, scale: 0.9 }}
              transition={{ delay: source.delay, duration: 0.45, type: 'spring' }}
              className="bg-card rounded-xl px-3 py-2.5 shadow-sm border border-border flex items-center gap-3"
            >
              <div className={`w-11 h-11 rounded-lg overflow-hidden shrink-0 flex items-center justify-center ${source.type === 'icon' ? source.bg : 'bg-muted ring-1 ring-border'}`}>
                {source.type === 'image' ? (
                  <img src={source.image} alt={source.name} className="w-full h-full object-cover" />
                ) : (
                  source.icon
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-heading">{source.name}</p>
                <p className="text-[10px] text-muted-foreground">{source.subtitle}</p>
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={isActive ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: source.delay + 0.25, type: 'spring' }}
              >
                <Heart className="w-3.5 h-3.5 text-primary fill-primary" />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
